import React from "react";
import {
    PASSWORD_CONFIRM_MISMATCH,
    SERVER_ERROR,
} from "../constants";
import ICustomer from "../interfaces/ICustomer";
import IError from "../interfaces/IError";
import API from "../utils/OGSAPI";
import translateError from "../utils/translateError";
import validateEmail from "../utils/validateEmail";
import validatePassword from "../utils/validatePassword";
import validateUsername from "../utils/validateUsername";

interface IProps {
    onClose(): void;
    onRegister(customer: ICustomer): void;
}

interface IState {
    confirmPassword: string;
    confirmPasswordTouched: boolean;
    email: string;
    emailTouched: boolean;
    password: string;
    passwordTouched: boolean;
    registerError?: IError;
    submitting: boolean;
    username: string;
    usernameTouched: boolean;
}

export default class RegisterModal extends React.Component<IProps, IState> {
    public state: IState = {
        confirmPassword: "",
        confirmPasswordTouched: false,
        email: "",
        emailTouched: false,
        password: "",
        passwordTouched: false,
        submitting: false,
        username: "",
        usernameTouched: false,
    };
    public render(): JSX.Element {
        const { onClose } = this.props;
        const {
            confirmPassword,
            confirmPasswordTouched,
            username,
            email,
            emailTouched,
            password,
            passwordTouched,
            registerError,
            submitting,
        } = this.state;

        const usernameErrors = validateUsername(username);

        const emailErrors = validateEmail(email);
        const emailValid = emailErrors.length === 0;

        const passwordErrors = validatePassword(password);
        const passwordValid = passwordErrors.length === 0;

        const confirmPasswordErrors = validatePassword(confirmPassword);
        if (confirmPassword !== password) {
            confirmPasswordErrors.push({
                type: PASSWORD_CONFIRM_MISMATCH,
            });
        }
        const confirmPasswordValid = confirmPasswordErrors.length === 0;

        const canSubmitRegister = !submitting && (
            usernameErrors.length +
            emailErrors.length +
            passwordErrors.length +
            confirmPasswordErrors.length
        ) === 0;

        const emailInputClasses: string[] = ["input"];
        if (emailTouched && !emailValid) {
            emailInputClasses.push("is-danger");
        }

        const passwordInputClasses: string[] = ["input"];
        if (passwordTouched && !passwordValid) {
            passwordInputClasses.push("is-danger");
        }

        const confirmPasswordInputClasses: string[] = ["input"];
        if (confirmPasswordTouched && !confirmPasswordValid) {
            confirmPasswordInputClasses.push("is-danger");
        }

        return (
            <div className="modal is-active">
                <div className="modal-background" onClick={ onClose.bind(this) }></div>
                <div className="modal-card">
                    <div className="modal-card-head">
                        <div className="modal-card-title">Register</div>
                        <button className="delete" aria-label="close" onClick={ onClose.bind(this) }></button>
                    </div>
                    <div className="modal-card-body">
                        <form
                            onSubmit={ this.submitRegister.bind(this) }
                        >
                            { this.renderUsernameField() }
                            <div className="field is-horizontal">
                                <div className="field-label">
                                    <label
                                        className="label"
                                    >Email</label>
                                </div>
                                <div className="field-body">
                                    <input
                                        className={ emailInputClasses.join(" ") }
                                        type="text"
                                        placeholder="Email"
                                        value={ email }
                                        onChange={ (
                                            e: React.ChangeEvent<HTMLInputElement>,
                                        ): void => {
                                            const newEmail = e.target.value;
                                            this.setState({
                                                email: newEmail,
                                                emailTouched: true,
                                            });
                                        } }
                                    />
                                </div>
                            </div>
                            { (emailTouched && !emailValid) ? (
                                <div className="field help is-danger has-text-right">
                                    { translateError(emailErrors[0]) }
                                </div>
                            ) : undefined }

                            <div className="field is-horizontal">
                                <div className="field-label">
                                    <label
                                        className="label"
                                    >Password</label>
                                </div>
                                <div className="field-body">
                                    <input
                                        className={ passwordInputClasses.join(" ") }
                                        type="password"
                                        placeholder="Password"
                                        value={ password }
                                        onChange={ (
                                            e: React.ChangeEvent<HTMLInputElement>,
                                        ): void => {
                                            const newPassword = e.target.value;
                                            this.setState({
                                                password: newPassword,
                                                passwordTouched: true,
                                            });
                                        } }
                                    />
                                </div>
                            </div>
                            { (passwordTouched && !passwordValid) ? (
                                <div className="field help is-danger has-text-right">
                                    { translateError(passwordErrors[0]) }
                                </div>
                            ) : undefined }

                            <div className="field is-horizontal">
                                <div className="field-label">
                                    <label
                                        className="label"
                                    >Confirm Password</label>
                                </div>
                                <div className="field-body">
                                    <input
                                        className={ confirmPasswordInputClasses.join(" ") }
                                        type="password"
                                        placeholder="Confirm Password"
                                        value={ confirmPassword }
                                        onChange={ (
                                            e: React.ChangeEvent<HTMLInputElement>,
                                        ): void => {
                                            const newPassword = e.target.value;
                                            this.setState({
                                                confirmPassword: newPassword,
                                                confirmPasswordTouched: true,
                                            });
                                        } }
                                    />
                                </div>
                            </div>
                            { (confirmPasswordTouched && !confirmPasswordValid) ? (
                                <div className="field help is-danger has-text-right">
                                    { translateError(confirmPasswordErrors[0]) }
                                </div>
                            ) : undefined }

                            { registerError ? (
                                <div className="field help is-danger has-text-right">
                                    { translateError(registerError) }
                                </div>
                            ) : undefined }

                            <div className="has-text-right">
                                <button
                                    type="submit"
                                    className="button is-link"
                                    disabled={ !canSubmitRegister }
                                >
                                    { submitting ? (
                                        <>
                                            Registering..<i className="fa fa-spin fa-spinner"></i>
                                        </>
                                    ) : (
                                        "Register"
                                    ) }
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        );
    }
    protected submitRegister(e: Event): void {
        e.preventDefault();

        const { username, email, password } = this.state;
        const registerData = {
            email,
            marketingEmail: "0",
            password,
            province: "BC",
            username,
        };
        this.setState({
            registerError: undefined,
            submitting: true,
        });
        API.register(registerData).then((customer: ICustomer) => {
            this.setState({

            });
            this.props.onRegister(customer);
        }, (errors?: IError[]): void => {
            let registerError: IError = { type: SERVER_ERROR };
            if (errors && errors.length) {
                registerError = errors[0];
            }
            this.setState({
                registerError,
                submitting: false,
            });
        });
    }
    private renderUsernameField(): JSX.Element {
        const {
            username,
            usernameTouched,
        } = this.state;

        const usernameErrors = validateUsername(username);
        const usernameValid = usernameErrors.length === 0;

        const usernameInputClasses: string[] = ["input"];
        if (usernameTouched && !usernameValid) {
            usernameInputClasses.push("is-danger");
        }

        return (
            <>
                <div className="field is-horizontal">
                    <div className="field-label">
                        <label
                            className="label"
                        >Username</label>
                    </div>
                    <div className="field-body">
                        <input
                            className={ usernameInputClasses.join(" ") }
                            type="text"
                            placeholder="Username"
                            value={ username }
                            onChange={ (
                                e: React.ChangeEvent<HTMLInputElement>,
                            ): void => {
                                const newUsername = e.target.value;
                                this.setState({
                                    username: newUsername,
                                    usernameTouched: true,
                                });
                            } }
                        />
                    </div>
                </div>

                { (usernameTouched && !usernameValid) ? (
                    <div className="field help is-danger has-text-right">
                        { translateError(usernameErrors[0]) }
                    </div>
                ) : undefined }
            </>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/RegisterModal.tsx